import { syntaxTree } from "@codemirror/language";
import type { SyntaxNode } from "@lezer/common";
import { Decoration, type DecorationSet, type EditorState, EditorView, type Range, ViewPlugin, type ViewUpdate, WidgetType } from "@uiw/react-codemirror";

// The seven names @lezer/json gives a value, which is what an array's elements are told from its commas by.
const VALUES = new Set(["True", "False", "Null", "Number", "String", "Object", "Array"]);

export interface ContainerCount {
  count: number;
  unit: "key" | "element";
}

export interface CountPill {
  at: number;
  label: string;
}

// "1 key" and not "1 keys": the pill is a phrase about the container and not a number with a word set after it.
export const countLabel = ({ count, unit }: ContainerCount) => `${count} ${unit}${count === 1 ? "" : "s"}`;

// Where every container in the range is labelled and with what, in the order the document holds them.
export function countPills(state: EditorState, from: number, to: number): CountPill[] {
  const pills = new Map<number, string>();

  syntaxTree(state).iterate({
    from,
    to,
    enter: (node) => {
      const held = contents(node.node);
      if (!held) return;

      // A container closed on the line it opened on is labelled at its own closing brace, comma after it or not,
      // and one that runs past that line at the end of the line its opener sits on.
      const opener = state.doc.lineAt(node.from);
      // The last container written to a place wins it, which is the one that opened nearest what the pill sits after.
      pills.set(node.to <= opener.to ? node.to : opener.to, countLabel(held));
    },
  });

  return [...pills].map(([at, label]) => ({ at, label })).sort((first, second) => first.at - second.at);
}

// What the container holds, or nothing at all for a node that is not one.
function contents(node: SyntaxNode): ContainerCount | null {
  if (node.name === "Object") return { count: node.getChildren("Property").length, unit: "key" };
  if (node.name !== "Array") return null;

  let count = 0;
  for (let child = node.firstChild; child; child = child.nextSibling) if (VALUES.has(child.name)) count++;
  return { count, unit: "element" };
}

class CountWidget extends WidgetType {
  constructor(readonly label: string) {
    super();
  }

  eq(other: CountWidget): boolean {
    return other.label === this.label;
  }

  // The words are the span's `::after` and never its text, which is what puts them out of a selection's reach for good.
  toDOM(): HTMLElement {
    const pill = document.createElement("span");
    pill.className = "cm-container-count";
    pill.dataset.count = this.label;
    return pill;
  }
}

// `side: 1` keeps the label after a caret sitting in the same place, which at the end of a line is where one lands.
const widgetAt = (label: string) => Decoration.widget({ widget: new CountWidget(label), side: 1 });

function countWidgets(view: EditorView): DecorationSet {
  const marks: Range<Decoration>[] = [];
  const drawn = new Set<number>();

  // A container spanning two of the ranges is entered in both, and the second pill would only sit under the first.
  for (const { from, to } of view.visibleRanges) {
    for (const { at, label } of countPills(view.state, from, to)) {
      if (drawn.has(at)) continue;
      drawn.add(at);
      marks.push(widgetAt(label).range(at));
    }
  }

  return Decoration.set(marks, true);
}

// In the document beside the brace rather than out in a gutter, and rebuilt from what is on screen and nothing more.
export const CONTAINER_COUNTS = [
  ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = countWidgets(view);
      }

      update(update: ViewUpdate) {
        // The count is read off the syntax tree, which for a long document lands after the change that moved it.
        if (update.docChanged || update.viewportChanged || syntaxTree(update.startState) !== syntaxTree(update.state)) {
          this.decorations = countWidgets(update.view);
        }
      }
    },
    { decorations: (plugin) => plugin.decorations },
  ),
  EditorView.theme({
    ".cm-container-count::after": { content: "attr(data-count)" },
    ".cm-container-count": {
      display: "inline-block",
      margin: "0 0.5ch 0 1ch",
      padding: "0 0.6em",
      // No family of its own: the document's monospace is what the pill sits among and what it has to set beside.
      fontSize: "0.75em",
      lineHeight: "1.6",
      verticalAlign: "middle",
      color: "var(--mantine-color-dimmed)",
      backgroundColor: "var(--mantine-color-dark-4)",
      borderRadius: "var(--mantine-radius-xl)",
      // Nothing here is document text, so a drag over the line and the copy that follows both go straight past it.
      userSelect: "none",
      WebkitUserSelect: "none",
      pointerEvents: "none",
    },
  }),
];
